import { Injectable, inject } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslationService } from '../i18n/translation.service';

/**
 * Etiquetas del paginador de Angular Material resueltas por i18n.
 *
 * Se provee una sola vez a nivel de aplicacion (`app.config.ts`), de modo que
 * todo `mat-paginator` del ERP —el del `DataTableWrapperComponent` incluido—
 * toma sus textos del bundle activo (ES/EN) y no de literales del codigo.
 *
 * `MatPaginatorIntl` expone sus etiquetas como propiedades, no como getters:
 * Material las lee una vez al pintar. Por eso, al cambiar el idioma hay que
 * reasignarlas y avisar por `changes`, que es la senal que el paginador
 * escucha para volver a renderizar.
 *
 * El nombre de la clase se conserva por compatibilidad con el provider ya
 * declarado en `app.config.ts`.
 */
@Injectable()
export class SpanishPaginatorIntl extends MatPaginatorIntl {
  private readonly translation = inject(TranslationService);

  constructor() {
    super();
    this.applyLabels();
    this.translation.currentLang$.subscribe(() => {
      this.applyLabels();
      this.changes.next();
    });
  }

  /** Relee del bundle las etiquetas fijas del paginador. */
  private applyLabels(): void {
    this.itemsPerPageLabel = this.translation.translate('PAGINATION.PER_PAGE');
    this.nextPageLabel = this.translation.translate('PAGINATION.NEXT');
    this.previousPageLabel = this.translation.translate('PAGINATION.PREV');
    this.firstPageLabel = this.translation.translate('PAGINATION.FIRST');
    this.lastPageLabel = this.translation.translate('PAGINATION.LAST');
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const of = this.translation.translate('PAGINATION.OF');
    if (length === 0 || pageSize === 0) {
      return `0 ${of} ${length}`;
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    const endIndex =
      startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} ${of} ${length}`;
  };
}
